// src/components/About.jsx
import React from "react";
import { motion } from "framer-motion";
import aboutImage from "../assets/ai1.png"; // Assuming this is the child with toothpaste
import aboutImage2 from "../assets/about2.jpg"; // Assuming this is the image for the second section

const About = () => {
  const commonViewport = { once: true, amount: 0.3 }; // Less strict for smaller elements

  // List of additives to avoid
  const harmfulAdditives = [
    "SLS (Sodium Lauryl Sulfate)",
    "SLES (Sodium Laureth Sulfate)",
    "Parabens",
    "Triclosan",
    "Titanium dioxide",
    "Carrageenan",
  ];

  return (
    <section className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-16 lg:py-24 overflow-hidden relative">
      {/* Decorative background circles - enhance existing ones */}
      <motion.div
        className="absolute top-10 -left-10 w-48 h-48 bg-pink-200 rounded-full opacity-30 blur-3xl z-0"
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 0.3, scale: 1 }}
        transition={{ duration: 1.5, delay: 0.5 }}
        viewport={commonViewport}
      />
      <motion.div
        className="absolute bottom-10 -right-10 w-56 h-56 bg-blue-200 rounded-full opacity-30 blur-3xl z-0"
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 0.3, scale: 1 }}
        transition={{ duration: 1.5, delay: 0.7 }}
        viewport={commonViewport}
      />

      {/* Main content wrapper */}
      <div className="max-w-[80%] mx-auto px-6 sm:px-8 lg:px-[5%] relative z-10">
        {/* Section 1: Hero-like Introduction */}
        <div className="flex flex-col lg:flex-row items-center lg:items-center gap-12 lg:gap-16 mb-20 lg:mb-32">
          {/* Left Text Content */}
          <motion.div
            className="w-full lg:w-1/2 space-y-8 text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Welcome to the new daily dose of{" "}
              <span className="text-teal-600">fresh, fun</span> and seriously{" "}
              <span className="text-teal-600">effective oral care!</span>
            </motion.h2>

            <motion.p
              className="text-lg sm:text-xl text-gray-700 leading-relaxed mt-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              We’re here to shake up the way you think about toothpaste and
              mouthwash with products that are as lively as you are and backed
              by the expertise of trusted, passionate and caring dentists.
            </motion.p>
          </motion.div>

          {/* Right Image */}
          <motion.div
            className="relative w-full lg:w-1/2 flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <motion.img
              src={aboutImage}
              alt="Child with healthy teeth and Sridanth toothpaste"
              className="w-full max-w-sm sm:max-w-md lg:max-w-lg rounded-3xl shadow-2xl z-10 transform hover:scale-102 transition-transform duration-300"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
            {/* Decorative circles */}
            <motion.div
              className="absolute -top-6 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-pink-100 rounded-full opacity-70 blur-3xl"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 0.7, scale: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              viewport={commonViewport}
            />
            <motion.div
              className="absolute -bottom-8 left-0 w-28 h-28 sm:w-36 sm:h-36 bg-blue-100 rounded-full opacity-50 blur-3xl"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 0.5, scale: 1 }}
              transition={{ duration: 1, delay: 1 }}
              viewport={commonViewport}
            />
          </motion.div>
        </div>

        {/* Section 2: Sridanth Philosophy & Additives */}
        <div className="flex flex-col lg:flex-row-reverse items-start gap-12 lg:gap-16 mb-20 lg:mb-32">
          {/* Right Text Content (now on the right for reverse layout) */}
          <motion.div
            className="w-full lg:w-1/2 space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p>
              Tired of the outdated, high chemical loaded products dominating
              the shelves, we set out to create better alternatives - ones that
              combine modern science with the goodness of nature.
            </p>
            <p>
              <span className="font-bold text-teal-700">Sridanth</span>: Our
              range of toothpastes and mouthwashes are thoughtfully crafted by
              dental professionals who know what it takes to keep your smile
              healthy, bright and ready to take on the world.
            </p>
            <p>
              Using safe, effective ingredients that care for your teeth, gums,
              and overall oral health - without harsh & bad additives like:
            </p>

            {/* List of additives - improved readability */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-gray-800 text-base">
              {harmfulAdditives.map((additive, index) => (
                <motion.li
                  key={additive}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  viewport={commonViewport}
                >
                  <svg
                    className="w-5 h-5 text-red-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                  <span>{additive}</span>
                </motion.li>
              ))}
            </ul>
            <p>
              You deserve products that are gentle, safe, milder, truly clean,
              environmentally responsible and designed for modern lifestyles,
              without empty promises.
            </p>
          </motion.div>

          {/* Left Image (now on the left for reverse layout) */}
          <motion.div
            className="relative w-full lg:w-1/2 flex justify-center lg:justify-start"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <motion.img
              src={aboutImage2}
              alt="Family or diverse group smiling, representing general oral health"
              className="w-full max-w-sm sm:max-w-md lg:max-w-lg rounded-3xl shadow-2xl z-10 transform hover:scale-102 transition-transform duration-300"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
            {/* Decorative circles */}
            <motion.div
              className="absolute -top-8 left-0 w-28 h-28 sm:w-36 sm:h-36 bg-blue-100 rounded-full opacity-50 blur-3xl"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 0.5, scale: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              viewport={commonViewport}
            />
            <motion.div
              className="absolute -bottom-6 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-pink-100 rounded-full opacity-70 blur-3xl"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 0.7, scale: 1 }}
              transition={{ duration: 1, delay: 1 }}
              viewport={commonViewport}
            />
          </motion.div>
        </div>

        {/* Section 3: Final Message & Satisfaction Rate */}
        <motion.div
          className="w-full space-y-6 text-center lg:text-left text-base sm:text-lg text-gray-700 leading-relaxed mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p>None of our products are tested on animals.</p>
          <p>
            We’re all about boosting your confidence with fresh breath and
            squeaky-clean & Stronger teeth, so you can own every moment with
            energy and a winning smile. Healthy habits have never been this easy
            or exciting!
          </p>
          <p>
            For us, a smile isn’t just a sign of good health. It’s a reflection
            of confidence, care, and everyday wellbeing. With every tube and
            bottle, we’re here to help you smile brighter, fresher, and
            healthier.
          </p>
          <p className="font-semibold text-teal-700 text-xl">
            Join the movement and make your oral care effortlessly awesome.
          </p>

          {/* 98% Badge - Centered on small screens, left-aligned on large screens */}
          <motion.div
            className="inline-flex items-center gap-4 bg-gradient-to-r from-teal-50 to-emerald-50 
                px-8 py-4 rounded-full border border-teal-200 shadow-lg mt-8 transform hover:scale-105 transition-transform duration-300"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            viewport={commonViewport}
          >
            <div className="flex items-center justify-center w-12 h-12 bg-teal-100 rounded-full">
              <svg
                className="w-7 h-7 text-teal-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-800">98%</div>
              <div className="text-sm font-medium text-teal-700">
                Satisfaction Rate
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;